import { ref, readonly, computed, nextTick } from 'vue';

const draggingComponent = ref(null);
const droppingComponent = ref(null);

const dropped = ref([]);

const droppables = ref([]);
const hasDroppables = computed(() => droppables.value.length > 0);
const draggables = ref([]);
const hasDraggables = computed(() => draggables.value.length > 0);

const isDragging = computed(() => !!draggingComponent.value);
const isDropping = computed(() => !!droppingComponent.value);
const isDropped = id => dropped.value.map(({ drag }) => drag && drag.uuid).includes(id);
const isDroppedOn = id => dropped.value.map(({ drop }) => drop && drop.uuid).includes(id);

const onDrag = (dragging, component, isKeypress) => {
    if (isDropping.value && !dragging) return;
    if (!dragging && draggingComponent.value && draggingComponent.value.uuid !== component.uuid) return;
    draggingComponent.value = dragging ? component : null;
    if (dragging && component) {
        if (isDropped(component.uuid)) {
            dropped.value = dropped.value.filter(({ drag }) => drag.uuid !== component.uuid);
        }
        if (isKeypress && hasDroppables.value) {
            const firstAvailableDrop = droppables.value.find(drop => !drop.isDroppedOn && !drop.disabled);
            if (!firstAvailableDrop) return;
            nextTick(() => {
                firstAvailableDrop.focus();
                onDropping(true, firstAvailableDrop);
            });
        }
    }
};
const onDropping = (dropping, component) => {
    if (!dropping && droppingComponent.value && component.uuid !== droppingComponent.value.uuid) return;
    droppingComponent.value = dropping ? component : null;
};
const onDrop = (draggable, isKeypress, isDraglist) => {
    if (!isDraglist) {
        dropped.value.push({
            drag: draggable,
            drop: droppingComponent.value
        });
        if (isKeypress && hasDraggables.value) {
            const firstAvailableDrag = draggables.value.find(drag => !drag.isDropped);
            if (!firstAvailableDrag) return Promise.resolve();
            nextTick(() => {
                firstAvailableDrag.focus();
                onDropping(true, firstAvailableDrag);
            });
        }
        return draggable.snapToDrop()
            .then(() => {
                draggingComponent.value = null;
                droppingComponent.value = null;
            });
    } else {
        if (draggingComponent.value) draggingComponent.value.setPosition(0, 0, false);
        draggingComponent.value = null;
        droppingComponent.value = null;
    }
};

const addDroppable = (droppable) => {
    droppables.value.push(droppable);
};
const removeDroppable = (droppable) => {
    droppables.value = droppables.value.filter(d => d.uuid !== droppable.uuid);
};
const addDraggable = (draggable) => {
    draggables.value.push(draggable);
};
const removeDraggable = (draggable) => {
    draggables.value = draggables.value.filter(d => d.uuid !== draggable.uuid);
    if (draggingComponent.value && draggingComponent.value.uuid === draggable.uuid) {
        draggingComponent.value = null;
    }
};

const reset = (resetting) => {
    if (!(resetting && resetting.uuid)) return Promise.resolve();
    const index = dropped.value.findIndex(({ drag, drop }) => drag.uuid === resetting.uuid || drop.uuid === resetting.uuid);
    if (~index) {
        nextTick(() => dropped.value.splice(index, 1));
        return dropped.value[index].drag.resetPosition();
    } else if (resetting && resetting.resetPosition) {
        resetting.resetPosition();
    }
};

const sendDragToDrop = (draggable, droppable) => {
    return new Promise(resolve => {
        onDrag(true, draggable);
        nextTick(() => nextTick(() => onDropping(true, droppable)));
        nextTick(() => nextTick(() => nextTick(() => nextTick(() => {
            onDrop(draggable).then(() => {
                onDrag(false, draggable);
                resolve();
            });
        }))));
    });
};

export default readonly({
    dropped,
    isDragging,
    isDropping,
    isDropped,
    isDroppedOn,

    draggingComponent,
    droppingComponent,

    onDrag,
    onDropping,
    onDrop,

    droppables,
    addDroppable,
    removeDroppable,
    addDraggable,
    removeDraggable,
    hasDroppables,
    hasDraggables,

    reset,
    sendDragToDrop
});
